/**
 * Calendar Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useCalendar hook.
 *
 * CR-6435 / CR-6436: both portal views follow their design's layout — page
 * header, filter panel (header · sections · footer), view bar, results — with
 * the filters served by useCalendarFilters. Only the section body and the
 * page subtitle differ; the supplier view has no Add event, as before.
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  useCalendar,
  CalendarView,
  ListView,
  EventDetailModal,
  CreateEventModal,
  CalendarFiltersPanel,
  CalendarFilterSections,
  CalendarViewBar,
} from './calendar-module';
import { useIsCustomer } from '../hooks/usePortalMode';
import { useMenuSettings } from '../contexts/MenuSettingsContext';

const AUDIT_ONLY_EVENT_TYPES = ['audit_action_due'];

export default function Calendar() {
  const {
    // View states
    viewMode,
    calendarView,
    currentDate,
    setViewMode,
    setCalendarView,

    // Data
    loading,
    error,
    calendarEvents,
    allListEvents,
    paginatedEventsByMonth,

    // Filters
    activeEventTypes,
    eventPeriod,
    setEventPeriod,
    handleEventTypeToggle,
    filters,
    handleExportCsv,

    // Pagination
    currentPage,
    totalPages,
    setCurrentPage,

    // Modal states
    eventModal,
    isEditing,
    editForm,
    createModalOpen,
    setIsEditing,
    setEditForm,
    setCreateModalOpen,

    // Actions
    fetchEvents,
    handleEventClick,
    handleSaveEvent,
    handleCompleteEvent,
    handleDeleteEvent,
    handleOpenCreateModal,
    handleSelectSlot,
    handleCreateEvent,

    // Navigation
    handleNavigate,
    handleNavigateToday,
    handleNavigatePrevious,
    handleNavigateNext,
  } = useCalendar();

  const isCustomer = useIsCustomer();
  // Audit actions are generated for the customer side only, while Audit Lite is on.
  const { auditLiteEnabled } = useMenuSettings();
  const showAuditActions = isCustomer && auditLiteEnabled;
  // The design opens with the sections visible; Hide filters keeps the header.
  const [showFilters, setShowFilters] = useState(true);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
          <button
            onClick={fetchEvents}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const navigationControls = viewMode === 'calendar' && (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={handleNavigateToday}>
          Today
        </Button>
        <div className="flex items-center">
          <Button variant="ghost" size="icon" onClick={handleNavigatePrevious}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={handleNavigateNext}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <h2 className="text-lg font-semibold">
        {currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
      </h2>
      <div className="w-[120px]" /> {/* Spacer for balance */}
    </div>
  );

  const results = viewMode === 'calendar' ? (
    <CalendarView
      events={calendarEvents}
      view={calendarView}
      date={currentDate}
      onEventClick={handleEventClick}
      onSelectSlot={handleSelectSlot}
      onNavigate={handleNavigate}
    />
  ) : (
    <ListView
      allListEvents={allListEvents}
      paginatedEventsByMonth={paginatedEventsByMonth}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      onEventClick={handleEventClick}
    />
  );

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Calendar</h1>
        <p className="text-muted-foreground mt-1">
          {isCustomer ? 'View and manage supplier-related events' : 'View and manage your upcoming events'}
        </p>
      </div>

      {/* Filter header · sections · footer */}
      <CalendarFiltersPanel
        expanded={showFilters}
        onShowFilters={() => setShowFilters(true)}
        onHideFilters={() => setShowFilters(false)}
        onRefresh={fetchEvents}
        onExport={handleExportCsv}
        onClear={filters.clear}
        onApply={filters.apply}
        activeFilterCount={filters.activeCount}
      >
        <CalendarFilterSections
          variant={isCustomer ? 'customer' : 'supplier'}
          draft={filters.draft}
          onChange={filters.setField}
          options={filters.options}
          activeEventTypes={activeEventTypes}
          onEventTypeToggle={handleEventTypeToggle}
          hiddenEventTypes={showAuditActions ? [] : AUDIT_ONLY_EVENT_TYPES}
        />
      </CalendarFiltersPanel>

      {/* View bar */}
      <CalendarViewBar
        viewMode={viewMode}
        calendarView={calendarView}
        eventPeriod={eventPeriod}
        canAddEvent={isCustomer}
        onAddEvent={() => handleOpenCreateModal()}
        onViewModeChange={setViewMode}
        onCalendarViewChange={setCalendarView}
        onEventPeriodChange={setEventPeriod}
      />

      {/* Results */}
      <Card>
        {viewMode === 'calendar' && (
          <CardHeader className="pb-4">{navigationControls}</CardHeader>
        )}
        <CardContent>{results}</CardContent>
      </Card>

      {/* Event Details Modal */}
      <EventDetailModal
        open={eventModal.isOpen}
        event={eventModal.data}
        isEditing={isEditing}
        editForm={editForm}
        onClose={eventModal.close}
        onEdit={() => setIsEditing(true)}
        onCancelEdit={() => setIsEditing(false)}
        onSave={handleSaveEvent}
        onComplete={handleCompleteEvent}
        onDelete={handleDeleteEvent}
        onFormChange={setEditForm}
        readOnly={!isCustomer}
      />

      {/* Create Event Modal — only available in customer mode */}
      {isCustomer && (
        <CreateEventModal
          open={createModalOpen}
          editForm={editForm}
          onClose={() => setCreateModalOpen(false)}
          onCreate={handleCreateEvent}
          onFormChange={setEditForm}
        />
      )}
    </div>
  );
}
